import React from 'react';
import { observer } from 'mobx-react';
import { useFormikContext } from 'formik';
import { policyTypes } from '@ourbranch/policy-types';

import { ExpandableCard } from 'core/components/expandable-card';
import { useStore } from 'core/store/store.mobx';
import useBrandFeatures from 'core/hooks/use-brand-features';
import { useDiscounts } from 'offer/hooks/use-discounts';
import { useDisabledState } from 'common/disabled-context';
import { MultilineDiscount } from './multiline-discount';
import { Discount } from './discount';
import { Paperless } from './paperless';
import { BixMutualAffinity } from './bix-mutual-affinity';
import { Inventory } from './inventory';
import { BranchPlusMultiPolicyDiscount } from './branch-plus-multi-policy-discount';
import { ConnectedHomeDiscount } from './connected-home-discount';
import { BundleDiscount } from './bundle-discount';
import { GoodStudent } from './good-student';
import { DrivingCourse } from './driving-course';
import { CommunityDrive } from './community-drive';
import { NewHomeDiscount } from './new-home-discount';
import { FutureEffectiveAdvancedQuote } from './future-effective-advanced-quote-savings';
import { FarmDiscount, AutoHomeBundleDiscount, FarmAssociationDiscount } from './farm-discount';

const DiscountToComponent = {
  paperless_savings: Paperless,
  bix_mutual_affinity_savings: BixMutualAffinity,
  imagery_collection_savings: Inventory,
  connected_home_discount_savings: ConnectedHomeDiscount,
  multi_policy_savings: BranchPlusMultiPolicyDiscount,
  bundle_savings: BundleDiscount,
  good_student: GoodStudent,
  driving_course: DrivingCourse,
  community_drive: CommunityDrive,
  new_home_savings: NewHomeDiscount,
  future_effective_date_savings: FutureEffectiveAdvancedQuote,
  auto_home_savings: AutoHomeBundleDiscount,
  legacy_savings: FarmDiscount,
  military_savings: FarmDiscount,
  education_savings: FarmDiscount,
  crop_insurance_savings: FarmDiscount,
  traditions_savings: FarmDiscount,
  association_savings: FarmAssociationDiscount,
  claim_free_savings: FarmDiscount
};

const DiscountsCard = observer(() => {
  const discounts = useDiscounts();

  const activeDiscounts = discounts.filter(({ isActive }) => isActive).length;
  const { offer: store } = useStore();
  const { disabled } = useDisabledState();
  const { values } = useFormikContext();
  const selectedOption = values?.selectedOption || store.selectedOption;
  const bundleCurrentlySelected = [policyTypes.ARBundle, policyTypes.HABundle, policyTypes.CABundle].includes(
    selectedOption
  );

  const { autoDiscounts: brandAutoDiscounts } = useBrandFeatures();
  // #35626: a brand-declared auto discount list (CFM) has no multiline discount.
  const usesBrandAutoDiscounts = Boolean(brandAutoDiscounts) && selectedOption === policyTypes.Auto;

  const showMultilineDiscountComponent =
    store.hardRejectionOnOffer && !bundleCurrentlySelected && !usesBrandAutoDiscounts;

  // #34855 / #34854: an inland-marine-only or liability-only farm offers no discount; skip the empty card.
  if (discounts.length === 0 && !showMultilineDiscountComponent) {
    return null;
  }

  return (
    <div id="offer_discounts">
      <ExpandableCard title="Discounts" badge={`${activeDiscounts} of ${discounts.length}`}>
        {discounts.map(
          (
            { canEdit, label, isActive, amount, description, possibleSavings, appcuesId, id, htmlId, statusText },
            ix
          ) => {
            const Component = DiscountToComponent[id] || Discount;
            return (
              <Component
                key={ix}
                label={label}
                amount={amount}
                possibleSavings={possibleSavings}
                description={description}
                isActive={isActive}
                appcuesId={appcuesId}
                canEdit={canEdit}
                id={id}
                htmlId={htmlId}
                statusText={statusText}
                disabled={disabled}
              />
            );
          }
        )}
        {showMultilineDiscountComponent && <MultilineDiscount />}
      </ExpandableCard>
    </div>
  );
});

export default DiscountsCard;
